import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, RefreshCw } from "lucide-react";
import api, { tokenStorage } from "../../lib/api";
import { toast, Toaster } from "sonner";

export default function AdminLogin() {
  const [email, setEmail] = useState("admin@codeflarelabs.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ question: "", token: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const navigate = useNavigate();

  const loadCaptcha = async () => {
    try {
      const res = await api.get("/auth/captcha");
      setCaptcha({ question: res.data.question, token: res.data.captcha_token });
      setCaptchaAnswer("");
    } catch {
      toast.error("Failed to load captcha");
    }
  };

  useEffect(() => { loadCaptcha(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", {
        email,
        password,
        captcha_token: captcha.token,
        captcha_answer: captchaAnswer,
      });
      tokenStorage.set(res.data.access_token);
      tokenStorage.setEmail(res.data.user.email);
      tokenStorage.setUser(res.data.user);
      toast.success(`Welcome ${res.data.user.name || ""}!`);
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      // Refresh captcha on any failure (token may have been consumed/expired)
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-6 relative overflow-hidden">
      <Toaster theme="dark" position="top-center" />
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute inset-0 flame-radial" />
      <div className="relative w-full max-w-md glass rounded-3xl p-8" data-testid="admin-login-card">
        <h2 className="text-2xl font-bold tracking-wide mb-6">CODEFLARE <span className="flame-text">LABS</span></h2>
        <div className="flex items-center gap-2 mb-2 text-[#FF6B1A]">
          <Lock size={16} />
          <span className="text-xs font-bold uppercase tracking-widest">Admin Panel</span>
        </div>
        <h1 className="text-3xl font-bold mb-1">Sign in</h1>
       

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A]" data-testid="admin-email-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Password</label>
            <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A]" data-testid="admin-password-input" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Captcha — solve the math</label>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="px-4 py-3 rounded-xl bg-gradient-to-br from-[#FF6B1A]/15 to-[#3B82F6]/15 border border-white/10 font-mono text-lg font-bold tracking-wider select-none" data-testid="admin-captcha-question">
                {captcha.question || "…"}
              </div>
              <input
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                inputMode="numeric"
                pattern="-?[0-9]*"
                required
                placeholder="Answer"
                className="flex-1 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A]"
                data-testid="admin-captcha-input"
              />
              <button type="button" onClick={loadCaptcha} title="Refresh captcha" className="p-3 rounded-xl border border-white/10 hover:bg-white/5" data-testid="admin-captcha-refresh">
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          <button disabled={loading} type="submit" className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold disabled:opacity-50" data-testid="admin-login-btn">
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        
      </div>
    </div>
  );
}
